import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControlName, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Store } from '@ngrx/store';
import { cloneDeep, each, find, findIndex, findKey, includes, map as lodashMap } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { getCurrentUser, getSchool } from '../../../store';
import { studentEditableFieldMap } from '../../constants/paths/student-editable-map.constant';
import { SorterColumnDataType } from '../../constants/sorter-column-data-type.constant';
import { BatchEditService, IBatchEditStudentParams } from '../../services/batch-edit-service/batch-edit-service';
import { ISchool, TValidSchoolTypes } from '../../typings/interfaces/school.interface';
import { IUser, IUserMini } from '../../typings/interfaces/user.interface';
import { BaseModalComponent } from '../base-modal.component';
import { IBaseModalData } from '../modals.service';
import { IMilestoneEditable, MilestonesKeyMap } from './../../constants/milestones.constant';
import { FLAG_POINT_PEOPLE_TYPES } from './../../constants/point-people-types.constant';
import { BATCH_EDIT_MODAL_CONFIG } from './batch-edit-modal.config';

export interface IBatchEditorProjection {
  schoolId: string;
  projection: {
    _id: boolean;
    studentId: boolean;
    pointPeople: boolean;
  };
  joins: any[];
}

export interface IEditableFields {
  humanName: string;
  category?: string;
  schoolLevels?: string[];
}

@Component({
  selector: 'batch-edit-modal',
  template: require('./batch-edit-modal.component.html'),
  styles: [require('./batch-edit-modal.component.scss'), require('./batch-edit-modal.component.scss')],
})
export class BatchEditModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger, { static: false }) trigger: MatAutocompleteTrigger;

  editableFields: IEditableFields[];
  filteredEditableFields$: Observable<any>;
  currentUser: Partial<IUser>;
  currentSchool: ISchool;
  selection: any;
  field: any;
  fieldSelected: boolean;
  currentStudents;
  editLabel: string;
  schoolType: TValidSchoolTypes;
  selectedStudentIds: string[];

  form: FormGroup;
  searchField: FormControlName;
  dynamicField: any;
  valueSelected: any; // allows for null value in dropdown and button enable/disable (JE)
  // constants
  types;
  studentEditableFields;

  searchFieldInput = BATCH_EDIT_MODAL_CONFIG.input;
  destroy$: Subject<boolean> = new Subject<boolean>();
  readonly buttons = BATCH_EDIT_MODAL_CONFIG.buttons;

  title: string;
  itemCount: number;
  itemType: string;

  constructor(
    dialogRef: MatDialogRef<BatchEditModalComponent>,
    private formBuilder: FormBuilder,
    private batchEditService: BatchEditService,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: IBaseModalData,
  ) {
    super(dialogRef);
    this.types = SorterColumnDataType;
    this.studentEditableFields = studentEditableFieldMap;
  }

  ngOnInit(): void {
    this.data = cloneDeep(this.data);
    this.title = 'Batch Edit Field';
    this.selectedStudentIds = this.data.studentIds;
    this.itemCount = this.selectedStudentIds.length;
    this.itemType = 'student';
    this.form = this._buildForm();

    this.store.select(getCurrentUser).subscribe(user => {
      this.currentUser = user;
    });

    this.store
      .select(getSchool)
      .pipe(take(1))
      .subscribe(school => {
        const params: IBatchEditorProjection = {
          schoolId: school._id,
          projection: {
            _id: true,
            studentId: true,
            pointPeople: true,
          },
          joins: null,
        };
        this.schoolType = school.schoolType;
        this._getProjectedStudents(params);
      });

    this.editableFields = this.batchEditService.getEditableFields(this.currentUser, this.schoolType);
    this.filteredEditableFields$ = this._filterEditableFields();
    this.fieldSelected = false;
    this.valueSelected = false;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  _getProjectedStudents(params) {
    this.batchEditService.getStudentsPointPeople(params).subscribe(students => {
      this.currentStudents = students;
    });
  }

  // dependent ng5 services. don't move to body batchEditService
  _buildForm(): FormGroup {
    const form = this.formBuilder.group({
      searchField: [this.searchFieldInput.placeholder, Validators.required],
      /**
       * placeholder and validation are null due to not knowing ahead of time what form control will be rendered.
       * Also, Validators.required just checks to make the field has a value. That can be accomplished
       * via check on whether field has been selected and value chosen. So may not be necessary but leaving for now.
       * - JYR
       */
      dynamicField: [undefined, undefined],
    });
    return form;
  }

  _filterEditableFields() {
    return this.form.controls.searchField.valueChanges.pipe(
      startWith(''),
      map(val => {
        return this._filterInput(val);
      }),
    );
  }

  _filterInput(val: string) {
    return this.batchEditService.filterInput(val, this.editableFields);
  }

  public onSelection(): void {
    const searchFieldValue = this.form.controls.searchField.value;

    this.field = this.batchEditService.getField(searchFieldValue);
    this.fieldSelected = true;
    this.editLabel = this.getEditLabel(searchFieldValue);

    // unselects the value and prevents previous selection from persisting when switching fields
    this.valueSelected = false;
    this.dynamicField = this.field;

    // TODO: uncertain what intention is here. this.valueSelected is used to determine whether submit button is
    // enabled. onSelection determines whether the user has simply chosen a field to edit.  Keep for now... (JYR)
    // const { dataTypeOptions } = this.field;
    // this.valueSelected =
    //   (dataTypeOptions && dataTypeOptions.canBeNull) || (dataTypeOptions && dataTypeOptions.canBeRemoved);
  }

  public getEditLabel(searchFieldValue: string): string {
    // TODO: make this more robust for future options in this modal (JE)
    const milestoneHumanNames = lodashMap(Object.keys(MilestonesKeyMap), key => {
      return MilestonesKeyMap[key].humanName;
    });
    if (includes(milestoneHumanNames, searchFieldValue)) return 'STATUS';
    else return 'TO';
  }

  public clearPlaceholder(): void {
    this.form.controls.searchField.setValue('');
  }

  // TODO: `newValue` should be calculated in this method, not with 2-way data binding.
  // Refactor component to leverage the event emitted by the child component on new value. (CM)
  public onNewValueChange() {
    this.valueSelected = true;
  }

  public clearSelection(): void {
    this.field = null;
    this.dynamicField = null;
    this.valueSelected = false;
    setTimeout(() => {
      /**
       * Using `form.reset()` for some reason automatically triggers the filtering
       * function again, and does so using the last selected field as the new search term.
       * This results in the menu opening with just last selected field displayed in the dropdown options.
       * Manually deleting that does return the full menu.
       *
       * So instead of resetting form, just pass an empty string to ensure
       * that the full menu is returned - JYR
       */
      this.form.controls.searchField.setValue('');
      this.fieldSelected = false;
    }, 0);
  }

  // no pointPerson editing in v2, not moving to service for now - JYR
  _processMultiPointPersonUpdate(saveValue: IUserMini) {
    const removePp = saveValue === null;
    const updatedStudents = [];

    each(this.selectedStudentIds, (studentId: string) => {
      const storedStudent = find(this.currentStudents, (storedStudent: any) => includes(storedStudent._id, studentId));
      // do not overwrite non flag point people (CM)
      const nonFlagPp = storedStudent.pointPeople.reduce((acc, pP) => {
        const flagPp = includes(FLAG_POINT_PEOPLE_TYPES, pP.type);

        if (!flagPp) {
          acc.push(pP);
        }

        return acc;
      }, []);
      let updatedPp = [...nonFlagPp];

      // add user as point person for all flag types (CM)
      if (!removePp) {
        const updatedFlagPp = FLAG_POINT_PEOPLE_TYPES.map(ppType => {
          const pointPerson = {
            user: saveValue,
            type: ppType,
          };

          return pointPerson;
        });

        updatedPp = [...updatedFlagPp, ...updatedPp];
      }

      /**
       * each pointPerson array update could be different for each student in the batch.
       * passing along _id so each diff can be identified with the correct student
       */
      const updatedStudent = {
        _id: storedStudent._id,
        dataType: SorterColumnDataType.ARRAY,
        value: updatedPp,
      };

      updatedStudents.push(updatedStudent);
    });

    return updatedStudents;
  }

  _findField(formValue) {
    return find(this.studentEditableFields, (field: { type: string; humanName: string }) => {
      return field.humanName === formValue;
    });
  }

  // no pointPerson editing in v2, not moving to service for now - JYR
  _processSinglePointPersonUpdate(form, saveValue): any[] {
    const field = this._findField(form.value.searchField);
    const request = {
      user: saveValue,
      type: field.type,
    };
    let updatedStudent;
    const updatedStudents = [];
    const removePointPerson = request.user === null;

    each(this.selectedStudentIds, (studentId: string) => {
      const storedStudent = find(this.currentStudents, (storedStudent: any) => includes(storedStudent._id, studentId));
      let updatedPp = [];

      if (storedStudent.pointPeople) {
        const idx = findIndex(storedStudent.pointPeople, (pp: { type: string; user: IUserMini }) => {
          return pp.type === field.type;
        });

        if (removePointPerson) {
          updatedPp = storedStudent.pointPeople.filter(({ type }) => type !== field.type);
        } else if (idx === -1) {
          // pointPerson for type does not exist (CM)
          updatedPp = [...storedStudent.pointPeople, request];
        } else {
          // change pointPerson for type
          updatedPp = [...storedStudent.pointPeople];
          updatedPp[idx] = request;
        }

        /**
         * each pointPerson array update could be different for each student in the batch.
         * passing along _id so each diff can be identified with the correct student
         */
        updatedStudent = {
          _id: storedStudent._id,
          dataType: SorterColumnDataType.ARRAY,
          value: updatedPp,
        };
        updatedStudents.push(updatedStudent);
      }
    });
    return updatedStudents;
  }

  processForm(form: FormGroup): IBatchEditStudentParams['patch'] {
    const { path, dataType, humanName } = this.field;
    const {
      value: { searchField },
    } = form;
    const patch = {
      path: null,
      newValue: null,
      dataType,
      key: null,
    };
    const isUserChange = dataType === SorterColumnDataType.USER_MINI;
    const isBooleanChange = dataType === this.types.BOOLEAN_YES_NO;
    const isMilestoneChange = dataType === SorterColumnDataType.MILESTONE;
    const isSinglePointPersonUpdate =
      dataType === SorterColumnDataType.USER_MINI &&
      (searchField === this.studentEditableFields.academicPointPerson.humanName ||
        searchField === this.studentEditableFields.attendancePointPerson.humanName ||
        searchField === this.studentEditableFields.postSecPointPerson.humanName ||
        searchField === this.studentEditableFields.creditsPointPerson.humanName ||
        searchField === this.studentEditableFields.regentsPointPerson.humanName ||
        searchField === this.studentEditableFields.advisor.humanName ||
        searchField === this.studentEditableFields.guidanceCounselor.humanName);
    const isMultiPointPersonUpdate = searchField === this.studentEditableFields.primaryPointPerson.humanName;
    const saveValue = (this.dynamicField && this.dynamicField.saveValue) || this.dynamicField || null;

    // preprocessing patch value based on data types
    if (isUserChange) {
      if (isMultiPointPersonUpdate) {
        patch.newValue = this._processMultiPointPersonUpdate(saveValue);
      } else if (isSinglePointPersonUpdate) {
        patch.newValue = this._processSinglePointPersonUpdate(form, saveValue);
      } else {
        // standard user mini update (guidance counselor, advisor)
        patch.newValue = saveValue;
      }
    } else if (isBooleanChange) {
      patch.newValue = this.batchEditService.setBooleanPatch(saveValue);
    } else if (isMilestoneChange) {
      patch.newValue = saveValue === 'Complete' ? 'COMPLETE' : 'NOT_COMPLETE';

      patch.key = findKey(MilestonesKeyMap, (milestone: IMilestoneEditable) => {
        return milestone.humanName === humanName;
      });
    } else {
      patch.newValue = saveValue;
    }

    // no preprocessing of path string
    patch.path = path;
    return patch;
  }

  cancel(): void {
    super.close();
  }

  submit(form: FormGroup): void {
    const patch = this.processForm(form);
    super.close(patch);
  }
}
